import { forwardRef, useState, useRef, cloneElement, Children, useCallback, createContext, useContext, useMemo, useEffect, useLayoutEffect } from 'react';
import { useFloating, offset, shift, autoUpdate, useInteractions, useHover, safePolygon, useClick, useListNavigation, useDismiss, useRole, FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
import { Transition, animated, useTransition } from '@react-spring/web';
import _ from 'classnames';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Link, headerHeightContext as headerHeightContext$1, Icon, HelpTooltip } from '@platform/elements';
import { useWindowScroll, useMeasure } from 'react-use';
import { createPortal } from 'react-dom';
import { Link as Link$1, useLocation, matchPath } from '@interhyp/platform-router';
import { ChevronDown, ChevronUp, ChevronRight, ChevronLeft, CheckCircleFilled, EmergencyHomeFilled, Circle, PendingFilled } from '@interhyp/platform-icons';
import { createExtensionPoint } from '@interhyp/platform-plugin';
import { arrowDown, arrowRight } from '@platform/icons';
import AnimateHeight from 'react-animate-height';

var styles$q = {"menu":"menu_F6A4VcSeJ-","arrow":"arrow_8i5BM31PyK"};

const SubMenu = /*#__PURE__*/forwardRef(({
  children,
  items,
  ...props
}, ref) => {
  const [open, setOpen] = useState(false);
  const listRef = useRef([]);
  useRef(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const {
    strategy,
    x,
    y,
    refs,
    context,
    middlewareData
  } = useFloating({
    placement: 'bottom-start',
    open,
    onOpenChange: setOpen,
    strategy: 'fixed',
    middleware: [offset({
      mainAxis: 0,
      crossAxis: -10
    }), shift({
      padding: 5
    })],
    whileElementsMounted: autoUpdate
  });
  const {
    getFloatingProps,
    getReferenceProps,
    getItemProps
  } = useInteractions([useHover(context, {
    handleClose: safePolygon(),
    delay: 150
  }), useClick(context), useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: index => setActiveIndex(index),
    loop: true
  }), useDismiss(context)]);
  const target = /*#__PURE__*/cloneElement(children, getReferenceProps({
    ...children.props,
    ref: refs.setReference
  }));
  const listItems = Children.map(items, (child, index) => {
    return /*#__PURE__*/cloneElement(child, getItemProps({
      ref: node => listRef.current[index] = node
    }));
  });
  return /*#__PURE__*/jsxs(Fragment, {
    children: [target, /*#__PURE__*/jsx(Transition, {
      from: {
        opacity: 0
      },
      enter: {
        opacity: 1
      },
      leave: {
        opacity: 0
      },
      reverse: open,
      items: open,
      config: {
        duration: 150
      },
      children: ({
        opacity
      }, item) => item && /*#__PURE__*/jsx(animated.div, {
        ...getFloatingProps({
          ...props,
          ref: refs.setFloating,
          style: {
            zIndex: 2000,
            opacity: opacity.to({
              range: [0, 1],
              output: [0, 1]
            }),
            position: strategy,
            left: x ?? 0,
            top: y ?? 0,
            outline: 'none'
          }
        }),
        children: /*#__PURE__*/jsx("nav", {
          className: _(styles$q.menu, props.className),
          children: listItems
        })
      })
    })]
  });
});

const emptyWrapper = ({
  children
}) => {
  return /*#__PURE__*/jsx(Fragment, {
    children: children
  });
};
function useSubMenu(items) {
  const menuWrapper = useCallback(({
    children
  }) => {
    return /*#__PURE__*/jsx(SubMenu, {
      items: items,
      children: children
    });
  }, [items]);
  return items ? menuWrapper : emptyWrapper;
}

/**
 * @deprecated Use height context from platform elements
 */
const headerHeightContext = /*#__PURE__*/createContext({});

/**
 * @deprecated use hook from platform elements
 */
function useHeaderContextHeight() {
  const {
    height
  } = useContext(headerHeightContext) ?? {};
  return height;
}

var styles$p = {"toolbar":"toolbar_zeno1Mqlp-","separator":"separator_-e11b54aKi","label":"label_rjeCriv8V-"};

var styles$o = {"content":"content_aoo0FelkkS"};

const Toolbar = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, ref) => {
  return /*#__PURE__*/jsx("div", {
    ref: ref,
    ...props,
    className: props.className,
    children: /*#__PURE__*/jsx("div", {
      className: styles$o.content,
      children: children
    })
  });
});

const ContextToolbar = /*#__PURE__*/forwardRef(({
  label,
  children,
  separator,
  ...props
}, ref) => {
  return /*#__PURE__*/jsxs(Toolbar, {
    ref: ref,
    ...props,
    className: _(styles$p.toolbar, props.className, {
      [styles$p.separator]: separator
    }),
    children: [label && /*#__PURE__*/jsx("b", {
      className: styles$p.label,
      children: label
    }), children]
  });
});

var styles$n = {"footer":"footer_7u3nd2yvog","links":"links_A-QQE8cFKW","x":"x_wTXJbdIvG4","link":"link_YvKOGV7t1p","button":"button_cCgTm-WUdW","description":"description_JV-uwDxXxx"};

function isLink(item) {
  return item.href !== undefined;
}
const Footer = /*#__PURE__*/forwardRef(({
  children,
  copyrightInfo,
  configurableLinks = [],
  description,
  ...props
}, ref) => {
  const parsedCopyrightInfo = useMemo(() => {
    if (typeof copyrightInfo === 'string') {
      return copyrightInfo;
    }
    const {
      companyName
    } = copyrightInfo;
    const year = new Date().getFullYear().toString();
    return `© ${year} ${companyName}`;
  }, [copyrightInfo]);
  return /*#__PURE__*/jsxs("footer", {
    className: styles$n.footer,
    ...props,
    children: [/*#__PURE__*/jsx("strong", {
      children: parsedCopyrightInfo
    }), description && /*#__PURE__*/jsx("p", {
      className: styles$n.description,
      children: description
    }), configurableLinks.length > 0 && /*#__PURE__*/jsx("div", {
      className: styles$n.links,
      children: configurableLinks.map(link => {
        if (isLink(link)) {
          return /*#__PURE__*/jsx(Link, {
            inline: true,
            href: link.href,
            target: "_blank",
            rel: "noopener noreferrer",
            className: styles$n.link,
            children: link.label
          }, link.label);
        }
        return (
          /*#__PURE__*/
          // Custom button is needed because it has to look like a inline text link
          jsx("button", {
            type: "button",
            onClick: link.action,
            className: styles$n.button,
            children: link.label
          }, link.label)
        );
      })
    })]
  });
});

function useTop() {
  const {
    y
  } = useWindowScroll();
  const [top, setTop] = useState(0);
  const [direction, setDirection] = useState('down');
  const yBefore = useRef(y);
  useEffect(() => {
    if (y > yBefore.current) {
      setDirection('down');
    } else {
      setDirection('up');
    }
  }, [y]);
  useEffect(() => {
    yBefore.current = y;
  }, [y]);
  useEffect(() => {
    if (direction === 'down') {
      setTop(-80);
    } else {
      setTop(0);
    }
  }, [direction]);
  return top;
}

const headerContext = /*#__PURE__*/createContext({});

var styles$m = {"header":"header_8wQWTrH1qj"};

const Header = /*#__PURE__*/forwardRef(({
  autoHide,
  ...props
}) => {
  const contentRef = useRef(null);
  const {
    setRef
  } = useContext(headerContext);
  const {
    height
  } = useContext(headerHeightContext);
  const top = useTop();
  useEffect(() => {
    const {
      body
    } = document;
    body.style.setProperty('--navigation-height', `${height ?? 0}px`);
  }, [height]);
  useLayoutEffect(() => {
    if (contentRef.current) {
      setRef(contentRef);
      return () => setRef(null);
    }
  }, [setRef]);
  return /*#__PURE__*/jsx("header", {
    ref: contentRef,
    ...props,
    style: {
      top: autoHide ? top : 0
    },
    id: "header",
    className: _(styles$m.header, props.className)
  });
});

function useHeight(ref) {
  const [height, setHeight] = useState(0);
  const resizeObserver = useRef(new ResizeObserver(entries => {
    const element = entries[0];
    if (element) {
      setHeight(element.contentRect.height);
    }
  }));
  useEffect(() => {
    const element = ref?.current;
    const observer = resizeObserver.current;
    if (element) {
      observer.observe(element);
      return () => {
        observer.unobserve(element);
      };
    }
  }, [ref]);
  return height;
}

function HeaderContext({
  children
}) {
  const [ref, setRef] = useState(null);
  const value = useMemo(() => ({
    ref,
    setRef
  }), [ref, setRef]);
  const height = useHeight(ref);
  const heightValue = useMemo(() => ({
    height
  }), [height]);
  return /*#__PURE__*/jsx(headerHeightContext.Provider, {
    value: heightValue,
    children: /*#__PURE__*/jsx(headerHeightContext$1.Provider, {
      value: heightValue,
      children: /*#__PURE__*/jsx(headerContext.Provider, {
        value: value,
        children: children
      })
    })
  });
}

function HeaderPortal({
  children
}) {
  const header = useContext(headerContext);
  if (!header?.ref?.current) {
    return null;
  }
  return /*#__PURE__*/createPortal(children, header.ref.current);
}

var styles$l = {"logo":"logo_JBS9pi2pU2"};

const Logo = /*#__PURE__*/forwardRef(({
  src,
  to = '/',
  ...props
}, ref) => {
  return /*#__PURE__*/jsx(Link$1, {
    ...props,
    to: to,
    ref: ref,
    className: _(styles$l.logo, props.className),
    children: /*#__PURE__*/jsx("img", {
      alt: "logo",
      src: src,
      height: 35
    })
  });
});

var styles$k = {"toolbar":"toolbar_kjyMzVP7tU"};

const MainToolbar = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, ref) => {
  return /*#__PURE__*/jsx(Toolbar, {
    ref: ref,
    ...props,
    className: _(styles$k.toolbar, props.className),
    children: children
  });
});

var styles$j = {"menu":"menu_QupvOPlfVP"};

const Menu = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, ref) => {
  return /*#__PURE__*/jsx("nav", {
    ...props,
    className: _(styles$j.menu, props.className),
    "aria-label": "Main",
    children: /*#__PURE__*/jsx("ul", {
      children: children
    })
  });
});

var styles$i = {"button":"button_J84xzN-H3T"};

const ShowMoreItemsButton = /*#__PURE__*/forwardRef(({
  items,
  className,
  showDefaultLabel,
  ...props
}, ref) => {
  const {
    pathname
  } = useLocation();
  const activeItem = useMemo(() => items.find(item => !!matchPath(pathname, {
    exact: item.exact,
    path: item.to
  })), [items, pathname]);
  return /*#__PURE__*/jsxs("button", {
    ...props,
    role: "menuitem",
    "aria-current": activeItem ? 'page' : undefined,
    className: _(styles$i.button, className),
    ref: ref,
    type: "button",
    children: [/*#__PURE__*/jsx("span", {
      children: showDefaultLabel ? 'Weitere' : activeItem?.label
    }), /*#__PURE__*/jsx(ChevronDown, {
      className: styles$i.icon
    })]
  });
});

const useIsCurrentPath = () => {
  const {
    pathname
  } = useLocation();
  return useCallback(item => !!matchPath(pathname, {
    exact: item.exact,
    path: item.to
  }), [pathname]);
};

var styles$h = {"floating":"floating_0I7KtD0wrs","option":"option_pbR0m-DrBv","active":"active_YO1CEmPTRH","open":"open_v9lfOSGgVC"};

const MoreNavItems = /*#__PURE__*/forwardRef(({
  items,
  showDefaultLabel
}, ref) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const listItemsRef = useRef([]);
  const {
    refs,
    floatingStyles,
    context
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset({
      mainAxis: 4,
      alignmentAxis: 0
    }), shift({
      padding: 24
    })],
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate
  });
  const hover = useHover(context, {
    handleClose: safePolygon({
      buffer: 4
    })
  });
  const role = useRole(context, {
    role: 'menu'
  });
  const dismiss = useDismiss(context);
  const listNavigation = useListNavigation(context, {
    listRef: listItemsRef,
    onNavigate: setActiveIndex,
    activeIndex
  });
  const {
    getFloatingProps,
    getItemProps,
    getReferenceProps
  } = useInteractions([role, dismiss, listNavigation, hover]);
  const isCurrentPath = useIsCurrentPath();
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(ShowMoreItemsButton, {
      items: items,
      showDefaultLabel: showDefaultLabel,
      ref: refs.setReference,
      ...getReferenceProps({
        onClick: () => setIsOpen(true),
        className: _({
          [styles$h.open]: isOpen
        })
      })
    }), /*#__PURE__*/jsx(FloatingPortal, {
      children: isOpen && /*#__PURE__*/jsx(FloatingFocusManager, {
        context: context,
        children: /*#__PURE__*/jsx("div", {
          ref: refs.setFloating,
          style: floatingStyles,
          ...getFloatingProps({
            className: styles$h.floating
          }),
          children: items.map((item, index) => /*#__PURE__*/jsx(Link$1, {
            to: item.to,
            ...getItemProps({
              tabIndex: activeIndex === index ? 0 : -1,
              ref(node) {
                listItemsRef.current[index] = node;
              },
              onClick() {
                setIsOpen(false);
              },
              className: _(styles$h.option, {
                [styles$h.active]: isCurrentPath(item)
              })
            }),
            children: /*#__PURE__*/jsx("span", {
              children: item.label
            })
          }, item.to))
        })
      })
    })]
  });
});

var styles$g = {"navigationHintContainer":"navigationHintContainer_8-hrjqRSIY","hint":"hint_7NNSzQAHFI","simpleHint":"simpleHint_q57DAzu4ay","counterHint":"counterHint_7yWJ4glQBA"};

function NavigationHint({
  hintConfig
}) {
  const data = hintConfig.useHint();
  if (isCounterHint(data) && data.count > 0) {
    return /*#__PURE__*/jsx("span", {
      className: _(styles$g.hint, styles$g.counterHint),
      children: data.count
    });
  }
  if (isSimpleHint(data) && data.active) {
    return /*#__PURE__*/jsx("span", {
      className: _(styles$g.hint, styles$g.simpleHint)
    });
  }
  return null;
}
function isSimpleHint(hint) {
  return hint?.type === 'SIMPLE';
}
function isCounterHint(hint) {
  return hint?.type === 'COUNTER';
}

const navigationHintExtension = createExtensionPoint(Symbol('navigation-hint'), true);

function NavigationHintConnected({
  path,
  exact
}) {
  const hints = navigationHintExtension.useExtensions();
  // Only show hints for the current route
  const filteredHints = hints.filter(h => matchPath(path, {
    path: h.context ? `/${h.context}/:id${h.path}` : h.path,
    exact
  }));
  if (filteredHints.length === 0) {
    return null;
  }
  return /*#__PURE__*/jsx("span", {
    className: styles$g.navigationHintContainer,
    children: filteredHints.map((hint, index) => /*#__PURE__*/jsx(NavigationHint, {
      hintConfig: hint
    }, index))
  });
}

var styles$f = {"menuItem":"menuItem_OtJi6mjo-r","visible":"visible_8sbkln-juC","invisible":"invisible_nDCNx5LZP1"};

function NavigationLink({
  item
}) {
  const {
    pathname
  } = useLocation();
  const active = useMemo(() => !!matchPath(pathname, {
    exact: item.exact,
    path: item.to
  }), [item.exact, item.to, pathname]);
  return /*#__PURE__*/jsxs(Link$1, {
    to: item.to,
    role: "menuitem",
    "aria-current": active ? 'page' : undefined,
    className: _(styles$f.menuItem),
    children: [/*#__PURE__*/jsx("span", {
      className: styles$f.visible,
      children: item.label
    }), /*#__PURE__*/jsx("span", {
      className: styles$f.invisible,
      "aria-hidden": true,
      children: item.label
    }), /*#__PURE__*/jsx(NavigationHintConnected, {
      path: item.to,
      exact: item.exact
    })]
  });
}

const getElementSize = label => {
  const element = document.createElement('a');
  element.textContent = label;
  element.style.visibility = 'hidden';
  element.style.position = 'absolut';
  element.style.width = 'max-content';
  element.className = styles$f.menuItem;
  document.body.append(element);
  const width = element.clientWidth;
  element.remove();
  return width;
};

var styles$e = {"sizeContainer":"sizeContainer_p7ZOTuuzK4","menu":"menu_y0a8qfCfMH","trailing":"trailing_qKIwNIlwBa"};

// Hardcoded width of the "Weitere"-Button -> Quickfix
const MORE_ITEMS_BUTTON_WIDTH = 100;
function Navigation({
  items,
  alignment,
  children,
  className
}) {
  const [ref, {
    width
  }] = useMeasure();
  const sizes = useMemo(() => {
    const sizes = [];
    for (const [index, item] of items.entries()) {
      const calculatedWidth = getElementSize(item.label);
      if (index === 0) {
        sizes.push(calculatedWidth);
      } else {
        sizes.push(calculatedWidth + sizes[index - 1]);
      }
    }
    return sizes;
  }, [items]);
  const [visibleItems, dropdownItems] = useMemo(() => {
    const indexToSplit = sizes.findIndex(value => value > width - MORE_ITEMS_BUTTON_WIDTH);
    if (indexToSplit < 0) {
      return [items, []];
    }
    return [items.slice(0, indexToSplit), items.slice(indexToSplit)];
  }, [items, sizes, width]);
  return /*#__PURE__*/jsxs("nav", {
    className: _(styles$e.menu, {
      [styles$e.trailing]: alignment === 'trailing'
    }, className),
    children: [/*#__PURE__*/jsx("div", {
      ref: ref,
      className: styles$e.sizeContainer,
      children: /*#__PURE__*/jsxs("ul", {
        children: [visibleItems.map(item => /*#__PURE__*/jsx("li", {
          children: /*#__PURE__*/jsx(NavigationLink, {
            item: item
          })
        }, item.to)), dropdownItems.length > 0 && /*#__PURE__*/jsx(MoreNavItems, {
          items: dropdownItems,
          showDefaultLabel: visibleItems.length > 0
        })]
      })
    }), children]
  });
}

var styles$d = {"menuItem":"menuItem_pzKghidXao","subMenu":"subMenu_eggP4ud5Cx","line":"line_yUuMRRPrVn","indicator":"indicator_SWR6Rk3024","arrow":"arrow_JDGboqnXIx","bold":"bold_Sv0bnSeaA1","subMenuIcon":"subMenuIcon_0d2Kz7pzyj","icon":"icon_Wek4sCSryu"};

const MenuItem = /*#__PURE__*/forwardRef(({
  icon,
  active,
  indicator = 'line',
  children,
  to = '#',
  label,
  showSubMenuIcon = false,
  exact = true,
  ...props
}, ref) => {
  const SubMenu = useSubMenu(children);
  return /*#__PURE__*/jsx(SubMenu, {
    children: /*#__PURE__*/jsx(Link$1, {
      ...props,
      to: to,
      ref: ref,
      role: "menuitem",
      "aria-current": active ? 'page' : undefined,
      className: _(styles$d.menuItem, styles$d[indicator], {
        [styles$d.subMenu]: !!children
      }, props.className),
      children: /*#__PURE__*/jsxs(Fragment, {
        children: [icon && /*#__PURE__*/jsx(Icon, {
          icon: icon,
          className: styles$d.icon,
          size: 16
        }), label, (children || showSubMenuIcon) && /*#__PURE__*/jsx(Icon, {
          className: styles$d.subMenuIcon,
          icon: arrowDown,
          size: 10
        }), /*#__PURE__*/jsx("div", {
          className: styles$d.indicator
        }), /*#__PURE__*/jsx(NavigationHintConnected, {
          path: to,
          exact: exact
        })]
      })
    })
  });
});

var styles$c = {"spacer":"spacer_podlIusHDs"};

const MenuSpacer = /*#__PURE__*/forwardRef(({
  ...props
}, ref) => {
  return /*#__PURE__*/jsx("div", {
    ...props,
    className: _(styles$c.spacer, props.className),
    ref: ref
  });
});

var styles$b = {"item":"item_RL9FQpjgVN","icon":"icon_bTwma33LnA","preIcon":"preIcon_0DHMiSKlLy","content":"content_2YJi8m6zob","title":"title_LLYPLtu-VO","subtitle":"subtitle_xVu3DxHnCS"};

const SubMenuItem = /*#__PURE__*/forwardRef(({
  label,
  subtitle,
  icon,
  to,
  ...props
}, ref) => {
  return /*#__PURE__*/jsxs(Link$1, {
    to: to ?? '#',
    onClick: e => {
      if (!to) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    ...props,
    role: "menuitem",
    className: _(styles$b.item, props.className),
    ref: ref,
    children: [icon && /*#__PURE__*/jsx("div", {
      className: styles$b.preIcon,
      children: /*#__PURE__*/jsx(Icon, {
        size: 20,
        icon: icon
      })
    }), /*#__PURE__*/jsxs("div", {
      className: styles$b.content,
      children: [/*#__PURE__*/jsx("div", {
        className: styles$b.title,
        children: label
      }), subtitle && /*#__PURE__*/jsx("div", {
        className: styles$b.subtitle,
        children: subtitle
      })]
    }), /*#__PURE__*/jsx(Icon, {
      size: 12,
      className: styles$b.icon,
      icon: arrowRight
    })]
  });
});

var styles$a = {"toolbar":"toolbar_x4nuZqbvC6"};

const ProductToolbar = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, ref) => {
  return /*#__PURE__*/jsx(Toolbar, {
    ref: ref,
    ...props,
    className: _(styles$a.toolbar, props.className),
    children: children
  });
});

var styles$9 = {"menu":"menu_9CMwltFXsZ","separator":"separator_4ju9GdnQzV"};

const IconMenu = /*#__PURE__*/forwardRef(({
  children,
  separator,
  ...props
}, ref) => {
  return /*#__PURE__*/jsx("nav", {
    ...props,
    className: _(styles$9.menu, props.className, {
      [styles$9.separator]: separator
    }),
    "aria-label": "Main",
    children: /*#__PURE__*/jsx("ul", {
      children: children
    })
  });
});

var styles$8 = {"iconMenuItem":"iconMenuItem_1C2NrnT4wo","active":"active_tFKgVtmSsm","indicator":"indicator_YmDNyb1Y5L"};

var styles$7 = {"link":"link_HRKBzBLLQa","button":"button_DjolylF-WM"};

const Interactable = /*#__PURE__*/forwardRef((props, ref) => {
  if (props.href) {
    return /*#__PURE__*/jsx(Link$1, {
      ...props,
      className: _(styles$7.link, props.className),
      ref: ref,
      to: props.href,
      children: /*#__PURE__*/jsx(Fragment, {
        children: props.children
      })
    });
  }
  return /*#__PURE__*/jsx("button", {
    type: "button",
    ...props,
    className: _(styles$7.button, props.className),
    ref: ref,
    children: props.children
  });
});

const IconMenuItem = /*#__PURE__*/forwardRef(({
  icon,
  children,
  active,
  ...props
}, ref) => {
  return /*#__PURE__*/jsxs(Interactable, {
    ...props,
    ref: ref,
    role: "menuitem",
    "aria-current": active ? 'page' : undefined,
    className: _(styles$8.iconMenuItem, {
      [styles$8.active]: active
    }, props.className),
    children: [/*#__PURE__*/jsx(Icon, {
      icon: icon
    }), /*#__PURE__*/jsx("span", {
      children: children
    }), /*#__PURE__*/jsx("div", {
      className: _(styles$8.indicator, {
        [styles$8.active]: active
      })
    })]
  });
});

const Collapsible = /*#__PURE__*/forwardRef(({
  children,
  open,
  ...props
}, ref) => {
  return /*#__PURE__*/jsx(AnimateHeight, {
    height: open ? 'auto' : 0,
    children: /*#__PURE__*/jsx("div", {
      ...props,
      ref: ref,
      children: children
    })
  });
});

var styles$6 = {"list":"list_7kicv6LFSa"};

/**
 * @deprecated This exact component is also exported from @interhyp/platform-layout
 */
const List = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, ref) => {
  return /*#__PURE__*/jsx("ul", {
    ...props,
    ref: ref,
    className: _(styles$6.list, props.className),
    children: children
  });
});

const AnimatedList = /*#__PURE__*/forwardRef(({
  children,
  ...props
}, ref) => {
  const items = Children.toArray(children);
  const transitions = useTransition(items, {
    from: {
      opacity: 0,
      left: 60
    },
    enter: {
      opacity: 1,
      left: 0
    },
    leaver: {
      opacity: 0,
      left: 60
    },
    delay: 150,
    trail: 60
  });
  return /*#__PURE__*/jsx(List, {
    ...props,
    ref: ref,
    children: transitions(({
      opacity,
      left
    }, item) => /*#__PURE__*/jsx(animated.li, {
      style: {
        position: 'relative',
        opacity,
        left
      },
      children: item
    }))
  });
});

var styles$5 = {"listItem":"listItem_bOEeYYmf9H","collapsible":"collapsible_yfOt43UK8v","item":"item_kP05ek-AOK","active":"active_LeEpbNxXRy","content":"content_iLpFEzm-3b","icon":"icon_9-sbaH4-2t","mark":"mark_hjGWsdn4d-","text":"text_sGmuUNrY0-","contained":"contained_v5waCHyccG"};

/**
 * @deprecated This exact component is also exported from @interhyp/platform-layout
 */
const ListItem = /*#__PURE__*/forwardRef(({
  children,
  open,
  sublist,
  arrow,
  marked,
  active,
  variant = 'text',
  ...props
}, ref) => {
  const ArrowIcon = {
    up: ChevronUp,
    right: ChevronRight,
    down: ChevronDown,
    left: ChevronLeft
  }[arrow ?? 'right'];
  return /*#__PURE__*/jsxs("li", {
    className: styles$5.listItem,
    "aria-selected": active,
    children: [/*#__PURE__*/jsxs(Interactable, {
      ...props,
      ref: ref,
      className: _(styles$5.item, props.className, styles$5[variant], {
        [styles$5.active]: active
      }),
      children: [/*#__PURE__*/jsx("span", {
        className: styles$5.content,
        children: children
      }), arrow && /*#__PURE__*/jsx(ArrowIcon, {
        className: styles$5.icon
      }), marked && /*#__PURE__*/jsx("span", {
        className: styles$5.mark
      })]
    }), sublist && /*#__PURE__*/jsx(Collapsible, {
      className: styles$5.collapsible,
      open: open,
      children: sublist
    })]
  });
});

var styles$4 = {"header":"header_NzJ0sbuPJ-"};

/**
 * @deprecated This exact component is also exported from @interhyp/platform-layout
 */
const ListHeader = /*#__PURE__*/forwardRef(({
  icon,
  label,
  ...props
}, ref) => {
  return /*#__PURE__*/jsxs("div", {
    ...props,
    ref: ref,
    className: _(styles$4.header, props.className),
    children: [/*#__PURE__*/jsx(Icon, {
      icon: icon,
      size: 16
    }), /*#__PURE__*/jsx("b", {
      children: label
    })]
  });
});

var styles$3 = {"statusIcon":"statusIcon_MNbTeUi2Vn","error":"error_VEKYGgEp-b","disabled":"disabled_W-J-e-5UUW","pending":"pending_KvTIigb5fz","centerDot":"centerDot_ci-60F5RC6"};

const ICONS = {
  DONE: CheckCircleFilled,
  ERROR: EmergencyHomeFilled,
  OPEN: Circle,
  DISABLED: Circle,
  PENDING: PendingFilled
};
function StatusIcon({
  status,
  active = false
}) {
  const Icon = ICONS[status];
  return /*#__PURE__*/jsxs("div", {
    className: _(styles$3.statusIcon, {
      [styles$3.error]: status === 'ERROR',
      [styles$3.disabled]: status === 'DISABLED',
      [styles$3.pending]: status === 'PENDING'
    }),
    children: [/*#__PURE__*/jsx(Icon, {}), status === 'OPEN' && active && /*#__PURE__*/jsx("div", {
      className: styles$3.centerDot
    })]
  });
}

var styles$2 = {"line":"line_zHVcjS0-M0","error":"error_EjTDeLXM8X","disabled":"disabled_xmwOf9igl5"};

function StatusLine({
  status
}) {
  return /*#__PURE__*/jsx("div", {
    className: _(styles$2.line, {
      [styles$2.error]: status === 'ERROR',
      [styles$2.disabled]: status === 'DISABLED'
    })
  });
}

var styles$1 = {"verticalStepperItem":"verticalStepperItem_Ch2vMCY7xU","error":"error_eNGMqzmCVV","active":"active_BEVnkLHzgG","headline":"headline_sMoCtF8HE6","disabled":"disabled_vFp8Kts0vr","leadingContainer":"leadingContainer_yA8ZCv1r9f","line":"line_TWkVJD3iMh","content":"content_9L8bSWW5e0","withSubline":"withSubline_SxKZzqPFsD","subline":"subline_gC9wX8FHJ-","infoTooltip":"infoTooltip_TJvUKAcycU"};

function Wrapper({
  item,
  active,
  children
}) {
  const classes = _(styles$1.verticalStepperItem, {
    [styles$1.active]: active,
    [styles$1.error]: item.status === 'ERROR',
    [styles$1.disabled]: item.status === 'DISABLED'
  });
  if (item.status === 'DISABLED') {
    return /*#__PURE__*/jsx("div", {
      className: classes,
      children: children
    });
  }
  if (item.to) {
    return /*#__PURE__*/jsx(Link$1, {
      to: item.to,
      onClick: item.onClick,
      className: classes,
      children: children
    });
  }
  return /*#__PURE__*/jsx("button", {
    type: "button",
    onClick: item.onClick,
    className: classes,
    children: children
  });
}
function VerticalStepperItem({
  item,
  active = false,
  hideLeadingLine
}) {
  return /*#__PURE__*/jsxs(Wrapper, {
    item: item,
    active: active,
    children: [/*#__PURE__*/jsxs("div", {
      className: styles$1.leadingContainer,
      children: [/*#__PURE__*/jsx(StatusIcon, {
        status: item.status,
        active: active
      }), !hideLeadingLine && /*#__PURE__*/jsx(StatusLine, {
        status: item.status
      })]
    }), /*#__PURE__*/jsxs("div", {
      className: _(styles$1.content, item.subline && styles$1.withSubline),
      children: [/*#__PURE__*/jsx("p", {
        className: styles$1.headline,
        children: item.headline
      }), item.subline && /*#__PURE__*/jsx("p", {
        className: styles$1.subline,
        children: item.subline
      }), item.info && /*#__PURE__*/jsx(HelpTooltip, {
        content: item.info,
        className: styles$1.infoTooltip,
        offsetX: 0
      })]
    })]
  });
}

var styles = {"verticalStepper":"verticalStepper_Plxe-vfm3m"};

const VerticalStepper = /*#__PURE__*/forwardRef(({
  items,
  active
}, ref) => {
  return /*#__PURE__*/jsx("div", {
    ref: ref,
    className: styles.verticalStepper,
    children: items.map((item, index) => /*#__PURE__*/jsx(VerticalStepperItem, {
      item: item,
      active: item.id === active,
      hideLeadingLine: index === 0
    }))
  });
});

export { AnimatedList, Collapsible, ContextToolbar, Footer, Header, HeaderContext, HeaderPortal, IconMenu, IconMenuItem, List, ListHeader, ListItem, Logo, MainToolbar, Menu, MenuItem, MenuSpacer, Navigation, NavigationHintConnected, ProductToolbar, SubMenu, SubMenuItem, Toolbar, VerticalStepper, navigationHintExtension, useHeaderContextHeight, useSubMenu };
//# sourceMappingURL=index.js.map
